/**
 * Miniaturas do Desk Cloud (docs/spec/desk-cloud.md, "Miniaturas"): o navegador reduz a imagem a
 * 320 px no maior lado e manda ao servidor, no envio. Para as imagens de antes, que não têm, o dono
 * gera ao abrir o Desk Cloud, poucas de cada vez. Tudo aqui é melhor esforço: sem miniatura, o cartão
 * mostra a própria imagem.
 */
import { api } from '../api.js';
import { serverUrl } from '../serverUrl.js';
import { DESK_CLOUD_LIMITS, type DeskFile } from '../shared.js';
import { useApp } from '../store.js';

/** Os arquivos que já tentaram ganhar miniatura nesta visita (dar certo ou não). */
const tried = new Set<string>();
/** Quantas miniaturas de antes se geram ao mesmo tempo. */
const BACKFILL_PARALLEL = 3;

/** O arquivo leva miniatura: uma imagem de verdade (não SVG) e grande o bastante. */
export const wantsThumb = (file: { mime: string; size: number }): boolean =>
  file.mime.startsWith('image/') && file.mime !== 'image/svg+xml' && file.size >= DESK_CLOUD_LIMITS.thumbMinFileBytes;

/** A imagem de um arquivo local, carregada (também reduz a imagem da mesa: `fitImage`, em files.ts). */
export function loadImage(source: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(source);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('imagem não carregou'));
    };
    img.src = url;
  });
}

/** O canvas num arquivo do tipo pedido; o navegador que não grava o tipo devolve PNG. */
export function toBlob(canvas: HTMLCanvasElement, type: string, quality = 0.8): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * A miniatura da imagem: WebP (JPEG onde o navegador não grava WebP), até 320 px no maior lado. Null
 * se não der, ou se ficar maior que o limite ou que o próprio arquivo.
 */
export async function makeThumb(source: Blob): Promise<Blob | null> {
  try {
    const img = await loadImage(source);
    const side = DESK_CLOUD_LIMITS.thumbSide;
    const scale = Math.min(1, side / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
    let blob = await toBlob(canvas, 'image/webp');
    if (!blob || blob.type !== 'image/webp') blob = await toBlob(canvas, 'image/jpeg');
    if (!blob || blob.size > Math.min(DESK_CLOUD_LIMITS.thumbMaxBytes, source.size)) return null;
    return blob;
  } catch {
    return null;
  }
}

/** Gera e manda a miniatura de um arquivo recém-enviado; devolve o link dela, ou null. */
export async function attachThumb(file: DeskFile, source: Blob): Promise<string | null> {
  const token = useApp.getState().token;
  if (!token || !wantsThumb(file) || tried.has(file.id)) return null;
  tried.add(file.id);
  const thumb = await makeThumb(source);
  if (!thumb) return null;
  try {
    return (await api.uploadThumb(token, file.id, thumb)).thumbUrl;
  } catch {
    return null;
  }
}

/**
 * As imagens de antes, sem miniatura: o dono baixa cada uma e gera a dela, poucas de cada vez. Quem
 * chama relê a lista quando o servidor avisar (`files.changed`).
 */
export async function backfillThumbs(files: readonly DeskFile[], ownerId: string): Promise<void> {
  const queue = files.filter((file) => file.ownerId === ownerId && !file.thumbUrl && file.trashedAt === null && wantsThumb(file) && !tried.has(file.id));
  const worker = async () => {
    for (let file = queue.shift(); file; file = queue.shift()) {
      try {
        const response = await fetch(serverUrl(file.url));
        if (response.ok) await attachThumb(file, await response.blob());
      } catch {
        tried.add(file.id);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(BACKFILL_PARALLEL, queue.length) }, worker));
}
